import { type FunctionComponent, type HTMLAttributes, useContext } from 'react';
import { SearchVariantContext } from '../../../contexts/search/search-variant.context';
import { type TrackedEvent } from '../../../helpers/analytics/event-types';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { useTrackEvent } from '../../../hooks/analytics/analytics.hooks';
import { type ProductSearchVariant } from '../../../types/product.types';

export const DEFAULT_VARIANT_SIZE = 27;

type SwatchProps = {
	variant: ProductSearchVariant;
	variantSize?: number;
	testId?: string;
};

const Swatch: FunctionComponent<SwatchProps> = ({ variant, variantSize = DEFAULT_VARIANT_SIZE, testId }) => {
	const resolvedTitle = 'name' in variant ? variant.name : 'title' in variant ? variant.title : 'Variant';
	return (
		<div
			title={resolvedTitle}
			data-testid={testId}
			style={{
				backgroundColor: `#${variant.swatchColor}`,
				width: `${variantSize}px`,
				height: `${variantSize}px`,
				margin: '2px'
			}}
		/>
	);
};

export type SearchVariantProps = {
	variant: ProductSearchVariant;
	variantSize?: number;
	selectEvent?: TrackedEvent;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const SearchVariant: FunctionComponent<SearchVariantProps> = ({
	variant,
	className = '',
	variantSize = DEFAULT_VARIANT_SIZE,
	selectEvent
}) => {
	const { setHoveredVariant, setSelectedVariant, hoveredVariant, selectedVariant } = useContext(SearchVariantContext);
	const trackEvent = useTrackEvent();

	const isActive = (hoveredVariant ? hoveredVariant?.id : selectedVariant.id) === variant.id;

	const resolvedTitle = 'name' in variant ? variant.name : 'title' in variant ? variant.title : 'Variant';
	const handleSelect = () => {
		setSelectedVariant(variant);
		if (selectEvent) {
			trackEvent({ event: selectEvent });
		}
	};

	const handleMouseEnter = () => {
		setHoveredVariant(variant);
	};

	const handleMouseLeave = () => {
		setHoveredVariant();
	};

	return (
		<div
			aria-checked={isActive}
			tabIndex={0}
			className="flex bw2 br b--theme-white"
			onClick={handleSelect}
			onKeyDown={handleKeys(['Enter', ' '], handleSelect)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			data-testid={`variantContent-${variant.id}`}
			data-automation="search-finish-swatch"
			role="checkbox">
			<div className={`ba br1 pointer outline-0 bw1 ${isActive ? 'b--theme-primary' : 'b--theme-grey-lighter'} ${className}`}>
				<Swatch variant={variant} variantSize={variantSize} testId={`${resolvedTitle.toLowerCase()}-swatch-${variantSize}`} />
			</div>
		</div>
	);
};
